import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const StylePage = () => {
  const styleGuides = [
    {
      id: 1,
      title: 'Layering Bracelets Like a Pro',
      image: 'https://customer-assets.emergentagent.com/job_9ca05207-5a15-4698-a993-55e5ec27d3ee/artifacts/bopqzr53_0.jpg',
      excerpt: 'Master the art of bracelet stacking with our guide to creating the perfect layered look.',
      category: 'Styling Tips'
    },
    {
      id: 2,
      title: 'Gold vs Silver: Which Suits You?',
      image: 'https://images.unsplash.com/photo-1731406322274-fb018de6fa97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxnb2xkJTIwYnJhY2VsZXQlMjB3b21hbiUyMHdyaXN0JTIwZGFyayUyMGZhc2hpb258ZW58MHx8fHwxNzY4ODI5NDg4fDA&ixlib=rb-4.1.0&q=85',
      excerpt: 'Discover which metal tone complements your skin tone and personal style best.',
      category: 'Style Guide'
    },
    {
      id: 3,
      title: 'Everyday Elegance: Work to Weekend',
      image: 'https://images.unsplash.com/photo-1626122738142-bbd66f671eca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYnJhY2VsZXQlMjB3b21hbiUyMHdyaXN0JTIwZGFyayUyMGZhc2hpb258ZW58MHx8fHwxNzY4ODI5NDg4fDA&ixlib=rb-4.1.0&q=85',
      excerpt: 'Versatile pieces that transition seamlessly from office to evening out.',
      category: 'Lifestyle'
    },
    {
      id: 4,
      title: 'Caring for Your Waterproof Jewellery',
      image: 'https://images.unsplash.com/photo-1767381819098-4beaccc15a63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBnb2xkJTIwamV3ZWxyeSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2ODgyOTQ4Nnww&ixlib=rb-4.1.0&q=85',
      excerpt: 'Simple tips to keep your LuxuryStyle pieces looking stunning for years.',
      category: 'Care Guide'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Style Guide - Jewellery Styling Tips | LuxuryStyle</title>
        <meta 
          name="description" 
          content="Get inspired with our jewellery styling guides. Learn how to layer bracelets, choose between gold and silver, and care for your waterproof jewellery." 
        />
        <meta property="og:title" content="Style Guide - LuxuryStyle" />
        <meta property="og:description" content="Jewellery styling tips and inspiration from LuxuryStyle." />
        <link rel="canonical" href="https://luxurystyle.info/style" />
      </Helmet>

      <main data-testid="style-page" className="pt-20">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-[#0a0a0a] border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
              Inspiration
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mt-4 mb-4">
              Style Guide
            </h1>
            <div className="gold-divider mb-6" />
            <p className="text-white/60 max-w-2xl mx-auto">
              Discover styling tips, care guides, and inspiration to help you 
              make the most of your LuxuryStyle jewellery.
            </p>
          </div>
        </section>

        {/* Featured Article */}
        <section className="py-20 md:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative group">
                <img
                  src="https://customer-assets.emergentagent.com/job_9ca05207-5a15-4698-a993-55e5ec27d3ee/artifacts/bopqzr53_0.jpg"
                  alt="Layered gold bracelets styling guide"
                  className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-4 border border-[#D4AF37]/30 pointer-events-none" />
              </div>
              <div>
                <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
                  Featured
                </span>
                <h2 className="text-3xl md:text-4xl font-medium text-white mt-4 mb-6">
                  The Art of Layering
                </h2>
                <div className="gold-divider-left mb-6" />
                <p className="text-white/60 leading-relaxed mb-6">
                  Bracelet stacking is more than just putting on multiple pieces—it's about 
                  creating a harmonious look that tells your story. Here's how to master the art:
                </p>
                <ul className="space-y-4 text-white/60 mb-8">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#D4AF37]">•</span>
                    <span>Start with a statement piece as your anchor</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#D4AF37]">•</span>
                    <span>Mix different chain styles for visual interest</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#D4AF37]">•</span>
                    <span>Vary widths from delicate to bold</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#D4AF37]">•</span>
                    <span>Don't be afraid to mix metals</span>
                  </li>
                </ul>
                <Link
                  to="/jewellery?collection=bracelets"
                  className="btn-luxury-outline inline-flex items-center space-x-2"
                >
                  <span>Shop Bracelets</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Style Articles Grid */}
        <section className="py-20 md:py-32 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
                Explore More
              </span>
              <h2 className="text-3xl md:text-4xl font-medium text-white mt-4 mb-4">
                Style Inspiration
              </h2>
              <div className="gold-divider mb-6" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {styleGuides.map((guide) => (
                <article 
                  key={guide.id}
                  data-testid={`style-article-${guide.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden mb-6">
                    <img
                      src={guide.image}
                      alt={guide.title}
                      className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-[#D4AF37] text-black text-xs uppercase tracking-wider">
                        {guide.category}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-xl font-medium text-white group-hover:text-[#D4AF37] transition-colors mb-3">
                    {guide.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-4">
                    {guide.excerpt}
                  </p>
                  <span className="inline-flex items-center space-x-2 text-[#D4AF37] text-sm group-hover:translate-x-2 transition-transform">
                    <span>Read More</span>
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-32 bg-[#050505]">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl md:text-4xl font-medium text-white mb-6">
              Ready to Elevate Your Style?
            </h2>
            <p className="text-white/60 mb-8">
              Browse our collection and find the perfect pieces to express your unique style.
            </p>
            <Link
              to="/jewellery"
              data-testid="style-cta-shop"
              className="btn-luxury inline-flex items-center space-x-2"
            >
              <span>Shop Now</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default StylePage;
